import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { employeeMatches, employeeName } from "@/lib/employeeSearch";

// Searchable employee dropdown: type a name, staff no, department or division.
export const EmployeePicker = ({
    employees = [], value, onChange, placeholder = "Select employee", allLabel, disabled, className = "", testid = "employee-picker",
}) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const selected = employees.find((e) => e.id === value);
    const list = useMemo(() => employees.filter((e) => employeeMatches(e, q)).slice(0, 200), [employees, q]);

    const pick = (id) => { onChange(id); setOpen(false); setQ(""); };

    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
            <PopoverTrigger asChild disabled={disabled}>
                <button type="button" disabled={disabled} data-testid={testid}
                    className={`w-full flex items-center justify-between gap-2 border border-ink/15 bg-white px-3 py-2 text-sm text-left focus:border-klein focus:outline-none disabled:bg-ink/5 disabled:text-ink/60 ${className}`}>
                    <span className={`truncate ${selected ? "text-ink" : "text-ink/50"}`}>
                        {selected ? `${employeeName(selected)}${selected.employee_no ? ` · ${selected.employee_no}` : ""}` : (allLabel || placeholder)}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 text-ink/40 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[300px] rounded-none z-[100]">
                <div className="flex items-center gap-2 border-b border-ink/10 px-3">
                    <Search className="w-4 h-4 text-ink/40" />
                    <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`}
                        placeholder="Name, staff no, department, division, phone…" className="w-full py-2.5 text-sm bg-transparent focus:outline-none" />
                </div>
                <div className="max-h-72 overflow-y-auto py-1" data-testid={`${testid}-list`}>
                    {allLabel && (
                        <Option active={!value} onClick={() => pick("")} testid={`${testid}-option-all`}>
                            <span className="text-ink/70">{allLabel}</span>
                        </Option>
                    )}
                    {list.map((e) => (
                        <Option key={e.id} active={e.id === value} onClick={() => pick(e.id)} testid={`${testid}-option-${e.id}`}>
                            <div className="min-w-0">
                                <div className="truncate text-ink">{employeeName(e)} <span className="text-ink/50 tabular-nums">{e.employee_no ? `· ${e.employee_no}` : ""}</span></div>
                                <div className="truncate text-xs text-ink/50">{[e.department, e.division].filter(Boolean).join(" · ") || "No department"}</div>
                            </div>
                        </Option>
                    ))}
                    {!list.length && <div className="px-3 py-4 text-sm text-ink/40" data-testid={`${testid}-empty`}>No staff match "{q}"</div>}
                </div>
            </PopoverContent>
        </Popover>
    );
};

const Option = ({ active, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-ink/5 ${active ? "bg-klein/5" : ""}`}>
        <Check className={`w-4 h-4 shrink-0 ${active ? "text-klein" : "opacity-0"}`} />
        {children}
    </button>
);

export default EmployeePicker;
